
import { Injectable, NgZone } from '@angular/core';
import { TelemetryService } from './telemetry/telemetry.service';
import { LoggingService } from './logging/logging.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PerformanceMetricsService {
  private markTimings: Map<string, number> = new Map();
  private measureTimings: Map<string, number> = new Map();
  private isMobileDevice: boolean;

  constructor(
    private telemetryService: TelemetryService,
    private logger: LoggingService,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Detect if user is on a mobile device
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Log the service name being used
    console.log('[PerformanceMetricsService] Initialized with service name:', telemetryService.getServiceName());
    
    // Initialize performance tracking when service is created
    this.initPerformanceTracking();
  }

  /**
   * Initialize performance tracking
   */
  private initPerformanceTracking(): void {
    // Track route changes to measure navigation performance
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.trackPageNavigation();
    });

    // Track initial page load metrics
    this.ngZone.runOutsideAngular(() => {
      if (typeof window !== 'undefined' && window.performance) {
        // Use setTimeout to ensure this runs after the page is rendered
        setTimeout(() => {
          this.trackInitialPageLoad();
        }, 0);
      }
    });
  }

  /**
   * Mark the start of a performance measurement
   * @param markName Unique name for this performance mark
   */
  public markStart(markName: string): void {
    this.markTimings.set(markName, performance.now());
    this.logger.debug('PerformanceMetricsService', `Mark start: ${markName}`);
  }

  /**
   * Mark the end of a performance measurement and record the duration
   * @param markName Name of the mark (should match a previous markStart call)
   * @param metricName Name of the metric for telemetry tracking
   * @param additionalData Additional data to include with the metric
   */
  public markEnd(markName: string, metricName: string, additionalData: Record<string, any> = {}): void {
    const startTime = this.markTimings.get(markName);
    if (!startTime) {
      this.logger.warn('PerformanceMetricsService', `No start mark found for: ${markName}`);
      return;
    }

    const duration = performance.now() - startTime;
    this.measureTimings.set(markName, duration);

    // Include mobile status in the telemetry data
    const telemetryData = {
      ...additionalData,
      durationMs: Math.round(duration),
      isMobileDevice: this.isMobileDevice
    };

    // Track in telemetry
    this.telemetryService.trackEvent(`performance_${metricName}`, telemetryData);

    this.logger.info('PerformanceMetricsService', `Performance: ${metricName} completed in ${Math.round(duration)}ms`, telemetryData);

    // Clean up the mark
    this.markTimings.delete(markName);
  }

  /**
   * Track a specific milestone in the app 
   * @param milestoneName Name of the milestone
   * @param data Additional data to include with the milestone
   */
  public trackMilestone(milestoneName: string, data: Record<string, any> = {}): void {
    const telemetryData = {
      ...data,
      timestamp: Date.now(),
      isMobileDevice: this.isMobileDevice
    };

    // Track in telemetry
    this.telemetryService.trackEvent(`milestone_${milestoneName}`, telemetryData);
    this.logger.info('PerformanceMetricsService', `Milestone: ${milestoneName}`, telemetryData);
  }

  /**
   * Track initial page load performance metrics from the browser's performance API
   */
  private trackInitialPageLoad(): void {
    if (typeof window === 'undefined' || !window.performance || !window.performance.timing) {
      return;
    }

    // Wait for the page to fully load before calculating metrics
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.trackInitialPageLoad());
      return;
    }

    const timing = performance.timing;
    
    // Core metrics
    const dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
    const tcpTime = timing.connectEnd - timing.connectStart;
    const requestTime = timing.responseStart - timing.requestStart;
    const responseTime = timing.responseEnd - timing.responseStart;
    const domParsingTime = timing.domInteractive - timing.responseEnd;
    const domContentLoadedTime = timing.domContentLoadedEventEnd - timing.domContentLoadedEventStart;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const timeToInteractive = timing.domInteractive - timing.navigationStart;
    const firstPaint = this.getFirstPaintTime();

    const metrics = {
      dnsLookupTime: dnsTime,
      tcpConnectionTime: tcpTime,
      requestTime: requestTime,
      responseTime: responseTime,
      domParsingTime: domParsingTime,
      domContentLoadedTime: domContentLoadedTime,
      totalLoadTime: loadTime,
      timeToInteractive: timeToInteractive,
      firstPaint: firstPaint,
      isMobileDevice: this.isMobileDevice
    };

    // Track the initial page load performance
    this.telemetryService.trackEvent('performance_page_load', metrics);
    this.logger.info('PerformanceMetricsService', 'Initial page load performance', metrics);
  }

  /**
   * Get first paint time if available from the Performance API
   */
  private getFirstPaintTime(): number | null {
    if (typeof window === 'undefined' || !window.performance || !window.performance.getEntriesByType) {
      return null;
    }

    const paintMetrics = performance.getEntriesByType('paint');
    const firstPaint = paintMetrics.find(entry => entry.name === 'first-paint');
    
    return firstPaint ? firstPaint.startTime : null;
  }

  /**
   * Track navigation performance metrics
   */
  private trackPageNavigation(): void {
    // Simple navigation timing
    const navigationStart = performance.now();
    
    // Use requestAnimationFrame to wait for the next frame
    // which indicates the page is visually updated
    requestAnimationFrame(() => {
      const firstFrameTime = performance.now() - navigationStart;
      
      const metrics = {
        timeToFirstFrame: firstFrameTime,
        isMobileDevice: this.isMobileDevice,
        path: this.router.url
      };

      this.telemetryService.trackEvent('performance_navigation', metrics);
      this.logger.info('PerformanceMetricsService', 'Navigation performance', metrics);
    });
  }
}